import React, { useEffect, useState } from 'react';
import { X, ArrowRight, ArrowLeft, Send, Star, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['bg-sky-500 hover:bg-sky-600', 'bg-amber-500 hover:bg-amber-600', 'bg-emerald-500 hover:bg-emerald-600', 'bg-pink-500 hover:bg-pink-600'];

export const KidQuiz = ({ token, item, onClose }) => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/activities/parent/items/${item.id}?token=${encodeURIComponent(token)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Could not load this activity'))))
      .then(setData).catch(e => setError(e.message));
  }, [item.id, token]);

  const qs = data?.questions || [];
  const q = qs[idx];
  const answered = Object.keys(answers).length;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/activities/parent/items/${item.id}/submit?token=${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || 'Could not send the answers');
      setResult(body);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const stars = result ? Math.max(1, Math.round((result.percentage / 100) * 3)) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-violet-900 text-white flex flex-col" data-testid="kid-quiz">
      <div className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-violet-200">{item.subject} · {item.code}</p>
          <h2 className="text-lg font-bold">{item.title}</h2>
        </div>
        <button onClick={onClose} className="rounded-full bg-white/10 p-2 hover:bg-white/20" aria-label="Close" data-testid="kid-quiz-close"><X className="h-5 w-5" /></button>
      </div>

      {error && <p className="mx-4 rounded-lg bg-rose-500/80 px-3 py-2 text-sm" data-testid="kid-quiz-error">{error}</p>}

      {!data && !error && <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>}

      {data && !result && q && (
        <div className="flex-1 flex flex-col px-4 pb-6 max-w-xl w-full mx-auto">
          <div className="h-2 rounded-full bg-white/20 mb-4"><div className="h-2 rounded-full bg-amber-400 transition-all" style={{ width: `${((idx + 1) / qs.length) * 100}%` }} /></div>
          <p className="text-sm text-violet-200 mb-2">Question {idx + 1} of {qs.length}</p>
          <p className="text-2xl sm:text-3xl font-bold leading-snug mb-6" data-testid="kid-quiz-question">{q.text}</p>
          <div className="space-y-3">
            {(q.options || []).map((opt, i) => {
              const letter = opt.trim()[0];
              const picked = answers[q.q_num] === opt;
              return (
                <button key={opt} onClick={() => setAnswers(a => ({ ...a, [q.q_num]: opt }))}
                  className={`w-full text-left rounded-2xl px-5 py-4 text-lg font-semibold shadow-lg transition-all active:scale-95 flex items-center justify-between ${COLORS[i % COLORS.length]} ${picked ? 'ring-4 ring-white scale-[1.03]' : answers[q.q_num] ? 'opacity-40' : 'opacity-95'}`}
                  data-testid={`kid-quiz-option-${q.q_num}-${letter}`}>
                  <span>{opt}</span>{picked && <CheckCircle2 className="h-7 w-7 shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="mt-auto pt-6 flex items-center justify-between gap-3">
            <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} className="rounded-full bg-white/10 px-4 py-3 disabled:opacity-30 flex items-center gap-1" data-testid="kid-quiz-back"><ArrowLeft className="h-5 w-5" /> Back</button>
            {idx < qs.length - 1 ? (
              <button onClick={() => setIdx(i => i + 1)} disabled={!answers[q.q_num]} className="rounded-full bg-amber-400 text-violet-900 font-bold px-6 py-3 disabled:opacity-40 flex items-center gap-1" data-testid="kid-quiz-next">Next <ArrowRight className="h-5 w-5" /></button>
            ) : (
              <button onClick={submit} disabled={busy || answered < qs.length} className="rounded-full bg-emerald-400 text-violet-900 font-bold px-6 py-3 disabled:opacity-40 flex items-center gap-1" data-testid="kid-quiz-submit">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Submit
              </button>
            )}
          </div>
          {idx === qs.length - 1 && answered < qs.length && <p className="text-xs text-violet-200 mt-2 text-right">Answer every question to submit ({answered}/{qs.length})</p>}
        </div>
      )}

      {result && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="kid-quiz-result">
          <div className="flex gap-2 mb-4">{[0, 1, 2].map(i => <Star key={i} className={`h-12 w-12 ${i < stars ? 'text-amber-400 fill-amber-400' : 'text-white/30'}`} />)}</div>
          <p className="text-4xl font-black mb-1" data-testid="kid-quiz-score">{result.score} / {result.total_marks}</p>
          <p className="text-violet-200 mb-6">{result.percentage >= 80 ? 'Fantastic work!' : result.percentage >= 50 ? 'Well done — keep practising!' : 'Good try! Look at the answers together.'}</p>
          <div className="w-full max-w-md space-y-2 text-left text-sm max-h-60 overflow-y-auto">
            {(result.questions || []).map(rq => {
              const m = result.marks?.[String(rq.q_num)];
              return <div key={rq.q_num} className={`rounded-lg px-3 py-2 ${m?.score ? 'bg-emerald-500/30' : 'bg-rose-500/30'}`}><span className="font-semibold">Q{rq.q_num}.</span> {rq.text} <span className="block text-xs text-white/80">{m?.feedback}</span></div>;
            })}
          </div>
          <button onClick={onClose} className="mt-6 rounded-full bg-white text-violet-900 font-bold px-8 py-3" data-testid="kid-quiz-finish">Finish</button>
        </div>
      )}
    </div>
  );
};
